import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Struct } from '@bufbuild/protobuf';
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowUpDown, BarChart3, Check, ChevronDown, Copy, FileText, Filter, Image as ImageIcon, List, MessageSquare, Move, PaintBucket, Paintbrush, Plus, Presentation, Replace, SeparatorHorizontal, Shapes, Sheet, Sigma, Snowflake, StickyNote, Table as TableIcon, Trash2, Type } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { useOfficeDocumentDrawer } from '../../../contexts/OfficeDocumentDrawerContext';
import { documentsApi } from '../../../lib/api/documentsApi.connect';
import { assistWriteHeaders } from '../../../lib/assistAttribution';
import { agentClient, responseDocumentClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
// Server flag key for the writing workspace (same value as RfiSectionGate's
// WRITING_WORKSPACE_FLAG, duplicated so chat doesn't import the RFI page tree).
const WRITING_WORKSPACE_FLAG = 'writing_workspace_v1';
const STALE_MESSAGE = 'The section changed since Assist read it. Ask Assist again.';
const DRAFTS_UNAVAILABLE_MESSAGE = 'Separate drafts are only available for response documents.';
// A human sentence for a failed apply, instead of the raw Connect error
// (`[invalid_argument] operation 2: …`). Status returns to pending so the user
// can retry or ask the assistant to revise.
function applyErrorMessage(err) {
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.InvalidArgument:
                return "Some of the proposed changes couldn't be applied to this document. Ask the assistant to revise them and try again.";
            case ConnectCode.NotFound:
                return 'This document no longer exists.';
            case ConnectCode.PermissionDenied:
                return "You don't have permission to edit this document.";
            default:
                return 'Something went wrong applying the changes. Please try again.';
        }
    }
    return 'Failed to apply the changes to the document.';
}
// The Accept/Reject/Comment decision is a one-time action, but the card is
// re-rendered (and can fully remount) as the chat streams, reloads a
// conversation, or scrolls — which would otherwise reset its local state to
// "pending" and re-offer the buttons on an action the user already took. Persist
// the taken action, keyed by the message part's stable id, so the card stays
// decided across remounts and page reloads.
const CARD_ACTIONS_KEY = 'shanks-document-write-actions';
function loadCardActions() {
    try {
        return JSON.parse(localStorage.getItem(CARD_ACTIONS_KEY) || '{}');
    } catch  {
        return {};
    }
}
function getCardAction(id) {
    if (!id) return undefined;
    return loadCardActions()[id];
}
function setCardAction(id, action) {
    if (!id) return;
    try {
        const map = loadCardActions();
        if (action === null) {
            delete map[id];
        } else {
            map[id] = action;
        }
        localStorage.setItem(CARD_ACTIONS_KEY, JSON.stringify(map));
    } catch  {
    /* localStorage unavailable — fall back to in-memory state only */ }
}
// XLSX / PPTX action sets. Detection keys off them: a presentation never carries
// xlsx actions, so pptx wins first, xlsx is the spreadsheet fallback, and anything
// else reads as a docx document. (add_image/replace_text are shared, so they're
// intentionally absent here — they can't disambiguate a type on their own.)
const XLSX_ACTIONS = new Set([
    'set_cell_value',
    'set_formula',
    'format_range',
    'set_number_format',
    'set_column_width',
    'set_row_height',
    'merge_cells',
    'auto_filter',
    'sort_range',
    'add_sheet',
    'rename_sheet',
    'add_chart',
    'freeze_panes'
]);
const PPTX_ACTIONS = new Set([
    'add_slide',
    'delete_slide',
    'duplicate_slide',
    'move_slide',
    'add_text_box',
    'add_bullets',
    'add_shape',
    'add_table',
    'set_slide_background',
    'set_speaker_notes'
]);
function docTypeOf(operations) {
    if (operations.some((op)=>PPTX_ACTIONS.has(op.action))) return 'pptx';
    if (operations.some((op)=>XLSX_ACTIONS.has(op.action))) return 'xlsx';
    return 'docx';
}
const TYPE_META = {
    docx: {
        Icon: FileText,
        eyebrow: 'PROPOSED DOCUMENT CHANGES',
        fallbackHeadline: 'Document changes',
        statusPhrase: 'finalized in the document once accepted',
        footerNote: 'Accepting applies and finalizes these changes. No second approval is needed.',
        acceptedText: 'Accepted — applied to the document'
    },
    xlsx: {
        Icon: Sheet,
        eyebrow: 'PROPOSED EDIT · SHOWN AS CELL CHANGES',
        fallbackHeadline: 'Spreadsheet changes',
        statusPhrase: 'written to the sheet once accepted',
        footerNote: 'Accepting writes these changes to the spreadsheet.',
        acceptedText: 'Accepted — written to the spreadsheet'
    },
    pptx: {
        Icon: Presentation,
        eyebrow: 'PROPOSED EDIT · APPLIED TO THE DECK',
        fallbackHeadline: 'Slide changes',
        statusPhrase: 'applied to the presentation once accepted',
        footerNote: 'Accepting applies these changes to the presentation.',
        acceptedText: 'Accepted — applied to the presentation'
    }
};
const ACCENT = {
    docx: {
        tile: 'bg-blue-500/10 text-blue-600 ring-blue-500/20 dark:text-blue-400',
        chip: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
        mark: 'bg-blue-500/80',
        faint: 'bg-foreground/15'
    },
    xlsx: {
        tile: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400',
        chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        mark: 'bg-emerald-500/80',
        faint: 'bg-foreground/10'
    },
    pptx: {
        tile: 'bg-orange-500/10 text-orange-600 ring-orange-500/20 dark:text-orange-400',
        chip: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
        mark: 'bg-orange-500/85',
        faint: 'bg-foreground/15'
    }
};
// A lucide icon per write action, shown in the row chip so each change reads at a
// glance rather than every row wearing the same "+". Unmapped actions fall back to
// the document-type glyph.
const OP_ICONS = {
    // docx
    insert_markdown: Plus,
    insert_content: Type,
    replace_text: Replace,
    insert_page_break: SeparatorHorizontal,
    // xlsx
    set_cell_value: Type,
    set_formula: Sigma,
    format_range: Paintbrush,
    set_number_format: Type,
    merge_cells: TableIcon,
    auto_filter: Filter,
    sort_range: ArrowUpDown,
    add_sheet: Plus,
    rename_sheet: Type,
    add_chart: BarChart3,
    add_image: ImageIcon,
    freeze_panes: Snowflake,
    // pptx
    add_slide: Plus,
    delete_slide: Trash2,
    duplicate_slide: Copy,
    move_slide: Move,
    add_text_box: Type,
    add_bullets: List,
    add_shape: Shapes,
    add_table: TableIcon,
    set_slide_background: PaintBucket,
    set_speaker_notes: StickyNote
};
// Human labels for the fallback rows we derive when the model didn't author a
// changeBlocks breakdown — one row per operation, no emoji.
const OP_ROW_LABELS = {
    insert_markdown: 'Insert content',
    insert_content: 'Insert styled content',
    replace_text: 'Replace text',
    insert_page_break: 'Page break',
    set_cell_value: 'Set cell value',
    set_formula: 'Set formula',
    format_range: 'Format range',
    set_number_format: 'Number format',
    set_column_width: 'Column width',
    set_row_height: 'Row height',
    merge_cells: 'Merge cells',
    auto_filter: 'Auto-filter',
    sort_range: 'Sort range',
    add_sheet: 'Add sheet',
    rename_sheet: 'Rename sheet',
    add_chart: 'Add chart',
    add_image: 'Insert image',
    freeze_panes: 'Freeze panes',
    // pptx
    add_slide: 'Add slide',
    delete_slide: 'Delete slide',
    duplicate_slide: 'Duplicate slide',
    move_slide: 'Move slide',
    add_text_box: 'Text box',
    add_bullets: 'Bullets',
    add_shape: 'Shape',
    add_table: 'Table',
    set_slide_background: 'Slide background',
    set_speaker_notes: 'Speaker notes'
};
// A bounded snippet: trimmed and capped so a huge insertion doesn't blow out the
// expanded row, while keeping enough to actually read (newlines preserved for the
// expanded, wrapped view).
function snippet(s, max) {
    const t = s.trim();
    return t.length > max ? `${t.slice(0, max).trimEnd()}…` : t;
}
// A slide index rendered for humans: 0-based on the wire, 1-based in copy.
function slideLabel(idx) {
    return typeof idx === 'number' && Number.isFinite(idx) ? `slide ${idx + 1}` : 'the last slide';
}
// A normalized 6-hex color (no `#`), or undefined if it isn't one — used to gate
// the background swatch so a malformed color never renders a broken chip.
function asHex(color) {
    if (typeof color !== 'string') return undefined;
    const h = color.trim().replace(/^#/, '');
    return /^[0-9a-fA-F]{6}$/.test(h) ? h.toUpperCase() : undefined;
}
// The human detail for a pptx op — the concrete thing it does to the deck (the
// slide's title, a shape's label, a table's shape) — or undefined for non-pptx
// actions, which fall through to the shared detail logic below.
function pptxDetail(action, p) {
    const lines = Array.isArray(p.lines) ? p.lines.filter((l)=>l != null) : [];
    switch(action){
        case 'add_slide':
            if (typeof p.title === 'string' && p.title.trim()) return snippet(p.title, 200);
            if (lines.length) return snippet(String(lines[0]), 200);
            return 'blank slide';
        case 'add_text_box':
            if (typeof p.text === 'string' && p.text.trim()) return snippet(p.text, 240);
            return lines.length ? snippet(String(lines[0]), 240) : '';
        case 'add_bullets':
            return `${lines.length} bullet${lines.length === 1 ? '' : 's'} · ${slideLabel(p.slideIndex)}`;
        case 'add_shape':
            {
                const shape = typeof p.shapeType === 'string' ? p.shapeType : 'rectangle';
                const text = typeof p.text === 'string' && p.text.trim() ? ` · “${snippet(p.text, 40)}”` : '';
                return `${shape}${text}`;
            }
        case 'add_table':
            {
                const rows = Array.isArray(p.rows) ? p.rows : [];
                const cols = rows.reduce((m, r)=>Math.max(m, Array.isArray(r) ? r.length : 0), 0);
                return rows.length ? `${rows.length}×${cols} · ${slideLabel(p.slideIndex)}` : slideLabel(p.slideIndex);
            }
        case 'add_image':
            return slideLabel(p.slideIndex);
        case 'set_slide_background':
            {
                const hex = asHex(p.color);
                return hex ? `#${hex} · ${slideLabel(p.slideIndex)}` : slideLabel(p.slideIndex);
            }
        case 'set_speaker_notes':
            {
                const notes = typeof p.notes === 'string' ? p.notes : typeof p.text === 'string' ? p.text : '';
                return notes ? snippet(notes, 280) : slideLabel(p.slideIndex);
            }
        case 'delete_slide':
        case 'duplicate_slide':
            return slideLabel(p.slideIndex);
        case 'move_slide':
            return `${slideLabel(p.slideIndex)} → position ${typeof p.toIndex === 'number' ? p.toIndex + 1 : '?'}`;
        default:
            return undefined;
    }
}
// Derive display rows from the raw operations, used when the model didn't send a
// changeBlocks breakdown so the card still reads as a list of changes. The detail
// keeps a bounded-but-full description; the row truncates it visually and expands
// to the whole thing on click.
function deriveRows(operations) {
    return operations.map((op)=>{
        const p = op.params ?? {};
        const pptx = pptxDetail(op.action, p);
        let detail = pptx ?? '';
        if (pptx === undefined) {
            if (typeof p.content === 'string') detail = snippet(p.content, 400);
            else if (typeof p.markdown === 'string') detail = snippet(p.markdown, 400);
            else if (typeof p.find === 'string') detail = `“${p.find}” → “${typeof p.replace === 'string' ? p.replace : ''}”`;
            else if (p.range !== undefined && p.value !== undefined) detail = `${p.range} = ${p.value}`;
            else if (p.range !== undefined) detail = String(p.range);
        }
        const swatch = op.action === 'set_slide_background' ? asHex(p.color) : undefined;
        return {
            label: OP_ROW_LABELS[op.action] ?? op.action,
            detail,
            action: op.action,
            swatch
        };
    });
}
// A detail is worth an expand affordance only when it doesn't fit on the one line
// the row shows collapsed.
function isExpandable(detail) {
    return !!detail && (detail.length > 44 || detail.includes('\n'));
}
/**
 * Renders a document write proposal from shanks as a review card: a type-accent
 * header, a per-change breakdown whose rows expand to show the full detail, a
 * type-shaped change preview (a page for docx, a grid for xlsx, a slide deck for
 * pptx) that marks where the edits land, and Accept / Reject / Comment actions.
 */ export function DocumentWriteCard({ output, partId, legacyPartId, toolCallId, requireSavedDecision = false, onAccept, onReject, onComment }) {
    const [commentText, setCommentText] = useState('');
    const [showComment, setShowComment] = useState(false);
    // Seed from the persisted decision so a remount doesn't re-offer the buttons.
    const [status, setStatus] = useState(()=>output.approvalState === 'accepted' || output.approvalState === 'rejected' ? output.approvalState : getCardAction(partId) ?? getCardAction(legacyPartId) ?? 'pending');
    const [execError, setExecError] = useState(null);
    const [preparing, setPreparing] = useState(false);
    // Set once the server says this isn't a response document: the card then behaves
    // exactly as it does with the writing-workspace flag off.
    const [assistUnavailable, setAssistUnavailable] = useState(false);
    const [softNote, setSoftNote] = useState(null);
    const writingOn = useFeatureFlag(WRITING_WORKSPACE_FLAG) === true;
    const [docName, setDocName] = useState(null);
    // Which breakdown rows are expanded to their full detail.
    const [expanded, setExpanded] = useState({});
    // Synchronous re-entry guard: state guards see a stale closure on a same-frame
    // double-click, so a ref (set immediately) is what actually blocks a second run.
    const actedRef = useRef(status !== 'pending');
    const savedProposal = Boolean(output.approvalConversationId && toolCallId);
    const legacyUnavailable = requireSavedDecision && !savedProposal;
    const assistActions = writingOn && savedProposal && !onAccept && !assistUnavailable && docTypeOf(output.operations ?? []) === 'docx';
    useEffect(()=>{
        if (output.approvalState === 'accepted' || output.approvalState === 'rejected') {
            actedRef.current = true;
            setStatus(output.approvalState);
            setPreparing(false);
            setExecError(null);
        }
    }, [
        output.approvalState
    ]);
    const instanceId = useId();
    const proposalId = partId || instanceId;
    const { openOffice, setProposal } = useOfficeDocumentDrawer();
    const sendFeedback = useChatFeedback();
    const previewOpenedRef = useRef(false);
    useEffect(()=>{
        const id = output.applyDocumentId || output.documentId;
        if (!id || !setProposal) return;
        const operations = output.operations;
        const pendingDraft = status === 'pending' && output.success === true && (operations?.length ?? 0) > 0;
        setProposal(proposalId, pendingDraft && operations ? {
            documentId: id,
            operations
        } : null);
        if (pendingDraft && operations && !previewOpenedRef.current) {
            previewOpenedRef.current = true;
            openOffice(id, {
                title: output.title || 'Document',
                docType: docTypeOf(operations)
            });
        }
        return ()=>setProposal(proposalId, null);
    }, [
        output.applyDocumentId,
        output.documentId,
        output.operations,
        output.success,
        output.title,
        status,
        setProposal,
        proposalId,
        openOffice
    ]);
    // Best-effort filename for the subtitle. Guarded so the mocked API in tests
    // (which only stubs applyDocumentOperations) simply yields no name.
    useEffect(()=>{
        let alive = true;
        // Resolve the filename against the apply id (a real shirohige UUID) — a
        // volume doc's vdoc_ id would 400 on getDocument.
        const id = output.applyDocumentId || output.documentId;
        if (!id || typeof documentsApi.getDocument !== 'function') return;
        documentsApi.getDocument(id).then((d)=>{
            if (alive) setDocName(d?.name ?? null);
        }).catch(()=>{
        /* name is optional; leave it off */ });
        return ()=>{
            alive = false;
        };
    }, [
        output.applyDocumentId,
        output.documentId
    ]);
    if (!output.success || !output.operations) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-xl border border-destructive/30 bg-destructive/5 p-3",
            children: /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-destructive",
                children: output.error || 'Failed to generate document operations.'
            })
        });
    }
    const operations = output.operations;
    // The id to apply/open/reload against. A proposal-volume document's write
    // carries a resolved shirohige UUID (applyDocumentId) distinct from the vdoc_
    // id the model uses; standalone docs fall back to documentId (already a UUID).
    const applyId = output.applyDocumentId || output.documentId;
    const docType = docTypeOf(operations);
    const meta = TYPE_META[docType];
    const accent = ACCENT[docType];
    const TypeIcon = meta.Icon;
    // Slides materialized by this edit — drives the deck-preview thumbnails and caption.
    const newSlideCount = operations.filter((op)=>op.action === 'add_slide' || op.action === 'duplicate_slide').length;
    // Every type derives its rows so each carries an action (for the icon) and a
    // background op carries its swatch — a model-authored changeBlocks list would drop
    // both — unless the model authored a richer breakdown for a docx/xlsx edit.
    const rows = docType === 'pptx' ? deriveRows(operations) : output.changes?.length ? output.changes : deriveRows(operations);
    const blockCount = output.blockCount ?? output.operationCount ?? operations.length;
    const headline = output.title || output.summary || meta.fallbackHeadline;
    const subtitle = docName ? `${docName} · ${meta.statusPhrase}` : meta.statusPhrase;
    // How many change locations the preview marks, and its caption.
    const markCount = rows.length;
    const railCaption = docType === 'pptx' ? newSlideCount > 0 ? `${newSlideCount} new slide${newSlideCount === 1 ? '' : 's'}` : 'slide edits' : docType === 'xlsx' ? `${markCount} cell change${markCount === 1 ? '' : 's'}` : `${markCount} edit${markCount === 1 ? '' : 's'}`;
    const handleAccept = async ()=>{
        // Guard against a second action (double-click, or a stale button before the
        // re-render): once a decision is taken or an apply is in flight, do nothing.
        if (actedRef.current || status !== 'pending' || preparing || legacyUnavailable) return;
        actedRef.current = true;
        if (onAccept) {
            onAccept(operations);
            setStatus('accepted');
            setCardAction(partId, 'accepted');
            return;
        }
        if (!applyId) {
            actedRef.current = false; // broken card — let the user see the error again
            setExecError('This document cannot be edited (it has no document id).');
            return;
        }
        setStatus('accepted');
        setExecError(null);
        setPreparing(true);
        try {
            // Apply server-side via ONLYOFFICE Document Builder (the browser connector
            // API is a paid feature the community docserver does not ship). The RPC
            // targets the shirohige UUID (applyId) directly, so there is no "wrong open
            // editor" race to guard against.
            let documentKey;
            if (output.approvalConversationId && toolCallId) {
                const response = await agentClient.decideDocumentProposal({
                    conversationId: output.approvalConversationId,
                    toolCallId,
                    decision: 'accepted'
                });
                if (response.decision === 'accepted' || response.decision === 'rejected') {
                    window.dispatchEvent(new CustomEvent('shanks-document-proposal-decided', {
                        detail: {
                            toolCallId,
                            decision: response.decision
                        }
                    }));
                }
                if (response.decision === 'rejected') {
                    setStatus('rejected');
                    setPreparing(false);
                    return;
                }
                if (response.decision !== 'accepted') throw new Error('Approval could not be confirmed.');
                documentKey = response.documentKey;
            } else {
                ({ documentKey } = await documentsApi.applyDocumentOperations(applyId, operations));
            }
            setPreparing(false);
            // Persist only after the apply actually succeeded, so a failed apply (below)
            // correctly leaves the card re-tryable.
            setCardAction(partId, 'accepted');
            // Open the document so the user actually SEES the applied change — the
            // drawer auto-opens on create but not when editing a document found later
            // (e.g. via list-office-documents in a new chat). If it's already open,
            // this is a no-op re-target of the same id.
            openOffice(applyId);
            // Tell the open editor (if any) to reload the freshly-written bytes.
            window.dispatchEvent(new CustomEvent('shanks-document-applied', {
                detail: {
                    documentId: applyId,
                    documentKey
                }
            }));
            // Continue THIS conversation with the accept outcome (scoped to the chat
            // that rendered this card).
            sendFeedback('User accepted the proposed document changes; they have been applied to the document.');
        } catch (err) {
            setPreparing(false);
            setExecError(savedProposal && !(err instanceof ConnectError && err.code === ConnectCode.InvalidArgument) ? 'Approval could not be confirmed. Retry safely to check the saved result.' : applyErrorMessage(err));
            setStatus('pending');
            setCardAction(partId, null); // apply failed — allow a retry
            actedRef.current = false;
        }
    };
    // WRT-08: apply the proposal as tracked changes (main) or into a new draft
    // through zou, then record the accept in shanks without re-applying it.
    const handleAssistApply = async (target)=>{
        if (actedRef.current || status !== 'pending' || preparing || legacyUnavailable) return;
        const conversationId = output.approvalConversationId;
        if (!conversationId || !toolCallId || !applyId) return;
        actedRef.current = true;
        setExecError(null);
        setSoftNote(null);
        setPreparing(true);
        try {
            const res = await responseDocumentClient.applyAssistOperations({
                documentId: applyId,
                baseVersionId: output.baseVersionId ?? output.base_version_id ?? '',
                operations: operations.map((op)=>({
                        action: op.action,
                        params: Struct.fromJson(op.params ?? {})
                    })),
                operationId: crypto.randomUUID(),
                target,
                draftName: '',
                proposalRef: {
                    conversationId,
                    toolCallId
                }
            }, {
                headers: assistWriteHeaders(conversationId, toolCallId)
            });
            if (res.stale) {
                setPreparing(false);
                setExecError(STALE_MESSAGE);
                actedRef.current = false;
                return;
            }
            const openId = target === 'new_draft' ? res.draft?.documentId || res.documentId : applyId;
            try {
                await agentClient.decideDocumentProposal({
                    conversationId,
                    toolCallId,
                    decision: 'accepted',
                    appliedExternally: true
                });
                window.dispatchEvent(new CustomEvent('shanks-document-proposal-decided', {
                    detail: {
                        toolCallId,
                        decision: 'accepted'
                    }
                }));
            } catch  {
                setSoftNote("The changes were applied, but the review decision couldn't be saved.");
            }
            setPreparing(false);
            setStatus('accepted');
            setCardAction(partId, 'accepted');
            openOffice(openId);
            window.dispatchEvent(new CustomEvent('shanks-document-applied', {
                detail: {
                    documentId: openId,
                    documentKey: res.documentKey
                }
            }));
            sendFeedback(target === 'new_draft' ? 'User kept the proposed document changes as a separate draft.' : 'User accepted the proposed document changes; they have been applied as tracked changes.');
        } catch (err) {
            setPreparing(false);
            actedRef.current = false;
            if (err instanceof ConnectError && err.code === ConnectCode.NotFound) {
                if (err.rawMessage.includes('not a response document')) {
                    // Not a response document: the legacy apply path takes over.
                    setAssistUnavailable(true);
                    if (target === 'main') {
                        void handleAccept();
                    } else {
                        setExecError(DRAFTS_UNAVAILABLE_MESSAGE);
                    }
                    return;
                }
            }
            setExecError(applyErrorMessage(err));
        }
    };
    const handleReject = async ()=>{
        if (actedRef.current || status !== 'pending' || preparing || legacyUnavailable) return;
        actedRef.current = true;
        if (output.approvalConversationId && toolCallId) {
            setPreparing(true);
            setExecError(null);
            try {
                const response = await agentClient.decideDocumentProposal({
                    conversationId: output.approvalConversationId,
                    toolCallId,
                    decision: 'rejected'
                });
                if (response.decision === 'accepted' || response.decision === 'rejected') {
                    window.dispatchEvent(new CustomEvent('shanks-document-proposal-decided', {
                        detail: {
                            toolCallId,
                            decision: response.decision
                        }
                    }));
                }
                if (response.decision === 'accepted') {
                    setStatus('accepted');
                    setCardAction(partId, 'accepted');
                    if (applyId) {
                        openOffice(applyId);
                        window.dispatchEvent(new CustomEvent('shanks-document-applied', {
                            detail: {
                                documentId: applyId,
                                documentKey: response.documentKey
                            }
                        }));
                    }
                    setPreparing(false);
                    return;
                }
                if (response.decision !== 'rejected') throw new Error('Rejection could not be confirmed.');
            } catch (err) {
                setExecError(err instanceof Error ? err.message : 'The decision could not be saved. Please retry.');
                setPreparing(false);
                actedRef.current = false;
                return;
            }
            setPreparing(false);
        }
        setStatus('rejected');
        setCardAction(partId, 'rejected');
        if (onReject) {
            onReject();
            return;
        }
        sendFeedback('User rejected the proposed document changes. Please ask what they would like changed.');
    };
    const handleComment = ()=>{
        if (actedRef.current || status !== 'pending' || preparing) return;
        if (!commentText.trim()) return;
        actedRef.current = true;
        setStatus('commented');
        setCardAction(partId, 'commented');
        if (onComment) {
            onComment(commentText.trim());
        } else {
            sendFeedback(`User feedback on proposed changes: ${commentText.trim()}`);
        }
        setShowComment(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.documentWriteCard,
        className: "overflow-hidden rounded-xl border border-border bg-card text-foreground",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-4 pb-3 pt-3.5",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex min-w-0 items-start gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: `mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset ${accent.tile}`,
                                    children: /*#__PURE__*/ _jsx(TypeIcon, {
                                        className: "h-[18px] w-[18px]"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-[11px] font-medium uppercase tracking-wider text-muted-foreground",
                                            children: meta.eyebrow
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1.5 text-balance text-[15px] font-semibold leading-snug text-foreground",
                                            children: headline
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 truncate text-[13px] text-muted-foreground",
                                            title: subtitle,
                                            children: subtitle
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            "data-testid": Testids.assistant.documentWriteOperationCount,
                            className: "shrink-0 rounded-md border border-border px-2.5 py-1 text-[12.5px] tabular-nums text-muted-foreground",
                            children: [
                                blockCount,
                                " change",
                                blockCount !== 1 ? 's' : ''
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-4 border-t border-border px-4 py-1.5",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "max-h-[300px] min-w-0 flex-1 overflow-auto",
                        children: rows.map((row, i)=>{
                            const Icon = row.action && OP_ICONS[row.action] || TypeIcon;
                            const detail = row.detail ?? '';
                            const canExpand = isExpandable(detail);
                            const open = !!expanded[i];
                            return /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: canExpand ? ()=>setExpanded((e)=>({
                                            ...e,
                                            [i]: !e[i]
                                        })) : undefined,
                                "aria-expanded": canExpand ? open : undefined,
                                className: `flex w-full items-start gap-3 border-b border-border/60 py-2.5 text-left last:border-b-0 ${canExpand ? 'cursor-pointer' : 'cursor-default'}`,
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `mt-px grid h-6 w-6 shrink-0 place-items-center rounded-md ${accent.chip}`,
                                        children: /*#__PURE__*/ _jsx(Icon, {
                                            className: "h-3.5 w-3.5"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "mt-[3px] w-[128px] shrink-0 truncate text-[13.5px] font-semibold text-foreground",
                                        children: row.label
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "mt-[3px] flex min-w-0 flex-1 items-start gap-1.5 text-[13.5px] text-muted-foreground",
                                        children: [
                                            row.swatch && /*#__PURE__*/ _jsx("span", {
                                                className: "mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm ring-1 ring-inset ring-black/10 dark:ring-white/15",
                                                style: {
                                                    backgroundColor: `#${row.swatch}`
                                                },
                                                "aria-hidden": true
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: open ? 'whitespace-pre-line break-words' : 'truncate',
                                                children: detail
                                            })
                                        ]
                                    }),
                                    canExpand && /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: `mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`,
                                        "aria-hidden": true
                                    })
                                ]
                            }, i);
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "hidden w-[104px] shrink-0 flex-col items-center pb-1 pt-2 sm:flex",
                        children: [
                            /*#__PURE__*/ _jsx(ChangePreview, {
                                docType: docType,
                                accent: accent,
                                marks: markCount,
                                newSlides: newSlideCount,
                                background: firstBackgroundHex(operations)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-2 text-center text-[11.5px] text-muted-foreground",
                                children: railCaption
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-1",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[12.5px] text-muted-foreground",
                        children: meta.footerNote
                    }),
                    applyId && /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "outline",
                        size: "sm",
                        "data-testid": Testids.assistant.documentWriteOpen,
                        onClick: ()=>openOffice(applyId, {
                                title: docName ?? 'Document',
                                docType
                            }),
                        className: "shrink-0",
                        children: [
                            /*#__PURE__*/ _jsx(TypeIcon, {
                                className: "h-4 w-4",
                                "aria-hidden": true
                            }),
                            status === 'pending' ? 'Preview' : 'Show document'
                        ]
                    })
                ]
            }),
            status === 'pending' && !showComment && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": Testids.assistant.documentWriteAccept,
                        onClick: assistActions ? ()=>void handleAssistApply('main') : handleAccept,
                        disabled: preparing || legacyUnavailable,
                        className: "inline-flex items-center gap-2 rounded-lg bg-foreground px-4 py-2 text-[13.5px] font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50",
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-4 w-4"
                            }),
                            assistActions ? 'Apply as tracked changes' : 'Accept as my change'
                        ]
                    }),
                    assistActions && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.assistant.documentWriteKeepAsDraft,
                        disabled: preparing || legacyUnavailable,
                        onClick: ()=>void handleAssistApply('new_draft'),
                        size: "xs",
                        variant: "secondary",
                        children: "Keep as separate draft"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: handleReject,
                        disabled: preparing || legacyUnavailable,
                        className: "inline-flex items-center rounded-lg border border-border px-4 py-2 text-[13.5px] font-medium text-foreground transition-colors hover:bg-muted",
                        children: assistActions ? 'Discard' : 'Reject'
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setShowComment(true),
                        "aria-label": "Comment on the proposed changes",
                        className: "ml-auto inline-flex items-center rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                        children: /*#__PURE__*/ _jsx(MessageSquare, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            showComment && status === 'pending' && /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx("textarea", {
                        value: commentText,
                        onChange: (e)=>setCommentText(e.target.value),
                        placeholder: "Tell the assistant what to change…",
                        className: "w-full resize-none rounded-md border border-border p-2 text-[13px] focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-ring",
                        rows: 2
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setShowComment(false),
                                className: "text-[13px] text-muted-foreground hover:text-foreground",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: handleComment,
                                disabled: !commentText.trim(),
                                className: "text-[13px] font-medium text-foreground hover:opacity-80 disabled:text-muted-foreground",
                                children: "Send feedback"
                            })
                        ]
                    })
                ]
            }),
            legacyUnavailable && status === 'pending' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: "This earlier proposal has no saved review status. Ask for a fresh proposal before applying it."
            }),
            preparing && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-[12.5px] text-muted-foreground",
                    children: status === 'pending' ? 'Saving review decision…' : 'Applying changes to the document…'
                })
            }),
            execError && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-destructive/20 bg-destructive/5 px-4 py-3",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-[12.5px] text-destructive",
                    children: execError
                })
            }),
            softNote && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                    children: softNote
                })
            }),
            status === 'accepted' && !execError && !preparing && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1.5 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Check, {
                        className: "h-3.5 w-3.5 text-foreground"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[12.5px] font-medium text-foreground",
                        children: meta.acceptedText
                    })
                ]
            }),
            status === 'rejected' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-[12.5px] font-medium text-muted-foreground",
                    children: "Changes rejected"
                })
            }),
            status === 'commented' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-[12.5px] font-medium text-muted-foreground",
                    children: "Feedback sent — waiting for the assistant to revise"
                })
            })
        ]
    });
}
// firstBackgroundHex returns the hex (no `#`) of the first set_slide_background op,
// so the deck preview can tint a slide the color the edit paints it.
function firstBackgroundHex(operations) {
    for (const op of operations){
        if (op.action === 'set_slide_background') {
            const hex = asHex(op.params?.color);
            if (hex) return hex;
        }
    }
    return undefined;
}
// distribute spreads `n` change marks across `total` slots so the preview reads as
// "edits scattered through the document" rather than a solid block at the top.
function distribute(n, total) {
    const k = Math.min(Math.max(n, 0), total);
    const out = new Set();
    for(let i = 0; i < k; i++)out.add(Math.floor((i + 0.5) / k * total));
    return out;
}
/**
 * The type-shaped, glanceable proxy for where the edit lands — a page for a
 * document, a cell grid for a spreadsheet, a slide deck for a presentation — with
 * the changed regions marked in the file type's accent. Not a real render; it just
 * signals "this edits a <type>, here" at a glance.
 */ function ChangePreview({ docType, accent, marks, newSlides, background }) {
    if (docType === 'pptx') return /*#__PURE__*/ _jsx(DeckPreview, {
        newSlides: newSlides,
        background: background,
        accent: accent
    });
    if (docType === 'xlsx') return /*#__PURE__*/ _jsx(SheetPreview, {
        marks: marks,
        accent: accent
    });
    return /*#__PURE__*/ _jsx(DocPreview, {
        marks: marks,
        accent: accent
    });
}
// A portrait page with a title bar and body lines; the edited lines are marked in
// the accent, spread down the page to suggest where the edits land.
function DocPreview({ marks, accent }) {
    const lines = 7;
    const hot = distribute(marks, lines);
    const widths = [
        78,
        92,
        64,
        88,
        72,
        84,
        58
    ];
    return /*#__PURE__*/ _jsx("div", {
        className: "aspect-[3/4] w-full overflow-hidden rounded-[3px] border border-border bg-background p-2",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full flex-col gap-[5px]",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "h-1.5 w-2/3 rounded-full bg-foreground/40"
                }),
                Array.from({
                    length: lines
                }).map((_, i)=>/*#__PURE__*/ _jsx("span", {
                        className: `h-[3px] rounded-full ${hot.has(i) ? accent.mark : accent.faint}`,
                        style: {
                            width: `${widths[i]}%`
                        }
                    }, i))
            ]
        })
    });
}
// A cell grid; the changed cells are marked in the accent, spread across the sheet.
function SheetPreview({ marks, accent }) {
    const cols = 5;
    const rows = 4;
    const total = cols * rows;
    const hot = distribute(marks, total);
    return /*#__PURE__*/ _jsx("div", {
        className: "aspect-[4/3] w-full overflow-hidden rounded-[3px] border border-border bg-background p-1.5",
        children: /*#__PURE__*/ _jsx("div", {
            className: "grid h-full grid-cols-5 grid-rows-4 gap-[3px]",
            children: Array.from({
                length: total
            }).map((_, i)=>/*#__PURE__*/ _jsx("span", {
                    className: `rounded-[1px] ${hot.has(i) ? accent.mark : accent.faint}`
                }, i))
        })
    });
}
// A small stack of 16:9 slide cards, one tinted when the edit sets a background.
function DeckPreview({ newSlides, background, accent }) {
    const shown = Math.min(Math.max(newSlides, 1), 3);
    return /*#__PURE__*/ _jsx("div", {
        className: "flex w-full flex-col gap-1.5",
        children: Array.from({
            length: shown
        }).map((_, i)=>/*#__PURE__*/ _jsx(SlideGlyph, {
                tint: i === 0 ? background : undefined,
                accent: accent
            }, i))
    });
}
function SlideGlyph({ tint, accent }) {
    const barColor = tint ? 'bg-white/90' : accent.mark;
    const lineColor = tint ? 'bg-white/60' : 'bg-foreground/25';
    return /*#__PURE__*/ _jsx("div", {
        className: "aspect-video w-full overflow-hidden rounded-[3px] border border-border bg-background p-1.5",
        style: tint ? {
            backgroundColor: `#${tint}`
        } : undefined,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full flex-col gap-1",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: `h-1 w-4/5 rounded-full ${barColor}`
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: `h-[3px] w-3/4 rounded-full ${lineColor}`
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: `h-[3px] w-2/3 rounded-full ${lineColor}`
                })
            ]
        })
    });
}
